import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import * as _ from 'underscore';
import moment from 'moment';
import 'chartjs-adapter-moment';
import { ChartConfiguration, ChartData, ChartDataset, ChartEvent, ChartOptions, Tooltip } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

@Component({
    selector: 'app-counter-party-ccr-ece-chart',
    templateUrl: './counter-party-ccr-ece-chart.component.html',
    styleUrls: ['./counter-party-ccr-ece-chart.component.scss'],
    standalone: false
})
export class CounterPartyCcrEceChartComponent {

    //@ViewChild(BaseChartDirective) chart: BaseChartDirective<'bar'> | undefined;

    public barChartOptions: any = {
        // We use these empty structures as placeholders for dynamic theming.
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        quarter: 'MMM YYYY'
                    }
                },
                grid: {
                    display: false
                }
            },
            y: {
                min: 125,
                max: 250,
                grid: {
                    display: false
                },
                ticks: {
                    includeBounds: true,
                    stepSize: 25,
                    padding: 5,
                }
            },
            yAxes: {
                type: 'linear',
                position: 'left', // `axis` is determined by the position as `'y'`
                grid: {
                    display: false
                },
                ticks: {
                    stepSize: 0.1,
                }
            },
        },
        plugins: {
            legend: {
                display: true,
            }, 
            tooltip: {
                callbacks: {
                    title: function (tooltipItems){
                        const date = tooltipItems[0].parsed.x;
                        return new Date(date).toLocaleDateString('en-EN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        }); 
                    }
                }
            }
        },
    };

    @Input()
    counterPartyCcrEceChartData: any[];

    @Input()
    periodLength?: string;

    tooltipLabel: string;
    tooltipDataSetName: string;
    tooltipDataValue: string;
    tooltipClickIndex: number;

    @Output()
    ccrTimeStepClick = new EventEmitter<any>();

    public chartOptions: any = {
        tooltips: {
            enabled: false
        },
        hover: {
            mode: 'index',
            onHover: this.getTooltipHoverHandler(this)
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [{
                type: 'time',
                distribution: 'linear',
                time: {
                    unit: 'month',
                    stepSize: 1,
                    tooltipFormat: 'DD/MM/YYYY',
                    displayFormats: {
                        month: 'MMM YYYY'
                    }
                },
                gridLines: {
                    drawOnChartArea: false,
                    tickMarkLength: 3
                },
                ticks: {
                    fontSize: 11
                }
            }],
            yAxes: [{
                gridLines: {
                    drawOnChartArea: false,
                    tickMarkLength: 3
                },
                ticks: {
                    min: 125,
                    max: 250,
                    stepSize: 25,
                    padding: 5,
                    fontSize: 11
                }
            }, {               
                gridLines: {
                    display: true,
                    drawTicks: true,
                    drawOnChartArea: false,
                    tickMarkLength: 3
                }
            }]
        }
    };
    //public chartLabels: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48, 51, 54, 57, 60, 63, 66,
    //    69, 72, 75, 78, 81, 84, 87, 90, 93, 96, 99, 102, 105, 108, 111, 114, 117, 120];
    public standardChartLabels: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 9, 12];
    public chartLabels: number[] = [0, 0.5, 1, 2, 3, 4, 5, 6, 9, 12];
    public dateFormatLabels: Date[] = [];
    public chartType = 'bar';
    public chartLegend = false;

    public chartData: any[] = [];

    colorArray = [{
        backgroundColor: '#1AA48C',
        hoverBackgroundColor: '#1AA48C',
        borderColor: '#1AA48C',
        hoverBorderColor: '#1AA48C'
    }];

    public Chart: any;

    public chartClicked(param: any): void {
    }

    public chartHovered(e: any): void {
    }

    public getTooltipHoverHandler(self: CounterPartyCcrEceChartComponent) {
        return function (evt: any, item: any) {
            const tooltipEl = document.getElementById('ccr-cce-chart-tooltip');
            if (!item.length || item.length === 0) {
                return;
            }
    
            tooltipEl.classList.add('tooltip-show');
            self.tooltipClickIndex = item[0]._index;
            self.tooltipLabel = moment(item[0]._model.label).format('DD/MM/YYYY');
            self.tooltipDataSetName = item[0]._model.datasetLabel;
            self.tooltipDataValue = self.chartData[0].data[self.tooltipClickIndex];
    
            const positionY = this.canvas.offsetTop;
            const positionX = this.canvas.offsetLeft;
            tooltipEl.style.left = (positionX + item[0]._model.x) - 10 + 'px';
            tooltipEl.style.top = (positionY + item[0]._model.y) - 86 + 'px';
        };
    }

    constructor() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes.periodLength) {
            return;
        }

        this.createDateFormatLabels(+changes.periodLength.currentValue);
        this.rotateData();
        this.getCcrYAxis();
    }

    ngOnInit() {
        this.rotateData();
        this.getCcrYAxis();
    }

    onExpiredBondsTooltipLinkClick(index: number) {
        const currentTimeStepData = _.find(this.counterPartyCcrEceChartData, x => x.timeStep === this.chartLabels[index]);
        const endDate = currentTimeStepData.timeStepDate;

        this.ccrTimeStepClick.emit({
            startDate: this.getCcrTimestepStartDate(index),
            endDate: endDate
        });
    }

    onAssociatedBondsTooltipLinkClick(index: number) {
        this.ccrTimeStepClick.emit({
            startDate: this.getCcrTimestepStartDate(index),
            endDate: null
        });
    }

    public getCcrTimestepStartDate(index: number): string {
        if (index > 0) {
            const previousTimeStepData = _.find(this.counterPartyCcrEceChartData, x => x.timeStep === this.chartLabels[index - 1]);
            return moment(previousTimeStepData.timeStepDate, 'YYYY-MM-DD', true).add(1, 'days')
                .format('YYYY-MM-DD');
        }
        return moment().format('YYYY-MM-DD');
    }

    public createDateFormatLabels(maxLabel: number) {
        let startDate = moment();
        if (this.counterPartyCcrEceChartData && this.counterPartyCcrEceChartData.length > 0) {
            startDate = moment(this.counterPartyCcrEceChartData[0].timeStepDate);
        }

        const indexOfMaxLabel = _.indexOf(this.standardChartLabels, maxLabel);
        const newChartLabels = this.standardChartLabels.slice(0, indexOfMaxLabel + 1);
        this.chartLabels = newChartLabels;
        this.dateFormatLabels = _.map(newChartLabels, x => {
            if (x === 0.5) {
                return startDate.clone().add(15, 'days').toDate();
            }
            return startDate.clone().add(x, 'months').toDate();
        });
    }

    public rotateData() {
        if (!this.counterPartyCcrEceChartData || this.counterPartyCcrEceChartData.length <= 0) {
            return;
        }
        this.chartData = [];
        const counterPartyList = _.groupBy(this.counterPartyCcrEceChartData, 'counterParty');

        for (const key of Object.keys(counterPartyList)) {
            let data = _.map(counterPartyList[key], x => x.ccr.toFixed(2));
            if (this.chartLabels.length !== data.length) {
                data = this.fixMissingTimesteps(counterPartyList[key]);
            }

            this.chartData.push({
                label: key,
                fill: false,
                lineTension: 0.5,
                borderWidth: 1.2,
                pointRadius: 2,
                pointHoverRadius: 3,
                data: data,
                backgroundColor: '#1AA48C',
                hoverBackgroundColor: '#1AA48C',
                borderColor: '#1AA48C',
                hoverBorderColor: '#1AA48C'
            });
        }
    }

    public fixMissingTimesteps(data: any[]): any[] {
        return _.map(this.chartLabels, x => {
            if (_.any(data, item => item.timeStep === x)) {
                return _.find(data, y => y.timeStep === x).ccr.toFixed(2);
            }
            return 0;
        });
    }

    public getCcrYAxis() {
        let maxCcr = Math.max(...this.counterPartyCcrEceChartData.map(x => x.ccr.toFixed(0)));
        const numberOfSteps = 10;

        maxCcr = this.getAddFivePercent(maxCcr);
        this.barChartOptions.scales.y.max = maxCcr;

        if (maxCcr <= 250) {
            this.barChartOptions.scales.y.ticks.stepSize = 25;
        } else if (maxCcr <= 500) {
            this.barChartOptions.scales.y.ticks.stepSize = 50;
        } else if (maxCcr <= 2000) {
            this.barChartOptions.scales.y.ticks.stepSize = 150;
        } else {
            this.barChartOptions.scales.y.ticks.stepSize = this.getNearestBaseValue(+maxCcr.toFixed(0), numberOfSteps);
        }

        this.chartOptions.scales.y.min = Math.min(...this.counterPartyCcrEceChartData.map(
            x => Math.floor(x.ccr)));
        this.chartOptions = Object.assign({}, this.chartOptions);
    }

    public getAddFivePercent(max: number): number {
        return max + ((max * 5) / 100);
    }

    public getNearestBaseValue(maxCcr: number, numberOfSteps: number): number {
        const stepValue = maxCcr / numberOfSteps;
        const length = maxCcr.toString().length;
        let str = '1';
        for (let i = 1; i < length - 1; i++) {
            str += '0';
        }

        return Math.ceil(stepValue / (+str)) * (+str);
    }

    public getEceYAxis() {
        const sampleRecord = _.find(this.counterPartyCcrEceChartData, x => x.ccr !== 0 && x.eceMUsd !== 0);
        const eceFor150CcrTickSize = ((150 - this.chartOptions.scales.yAxes[0].ticks.min) *
            (sampleRecord.eceMUsd / sampleRecord.ccr)).toFixed(0);
        this.chartOptions.scales.yAxes[1].ticks.min = -eceFor150CcrTickSize;
        this.chartOptions.scales.yAxes[1].ticks.max =
            +(((this.chartOptions.scales.yAxes[0].ticks.max - 150) * (sampleRecord.eceMUsd / sampleRecord.ccr)).toFixed(0));

        this.chartOptions = Object.assign({}, this.chartOptions);
    }
}